import { gql } from '@apollo/client';

/**
 * Step 5's documents live in their own file and are re-exported here, so no
 * screen has to know which file a document is in. Split by STEP rather than by
 * screen: this file is what Steps 1–4 shipped and is stable, and a reviewer
 * looking for what Step 5 added should not have to diff a thousand lines.
 */
export * from './queries.step5.js';
export * from './queries.step6.js';

const PHONE_FIELDS = gql`
  fragment PhoneFields on PhoneNumber {
    id
    number
    label
    isPrimary
    channels {
      id
      code
      labelFa
    }
  }
`;

export const CONTACTS_QUERY = gql`
  ${PHONE_FIELDS}
  query Contacts(
    $page: Int!
    $pageSize: Int!
    $search: String
    $statuses: [CollaborationStatus!]
    $deleted: Boolean
  ) {
    contacts(
      page: $page
      pageSize: $pageSize
      search: $search
      statuses: $statuses
      deleted: $deleted
    ) {
      totalCount
      page
      pageSize
      items {
        id
        kind
        displayName
        collaborationStatus
        subscriptionType
        photoUrl
        deletedAt
        specialty {
          id
          labelFa
        }
        fieldOfWork {
          id
          labelFa
        }
        primaryPhone {
          ...PhoneFields
        }
        tags {
          id
          name
          colorHex
        }
        updatedAt
      }
    }
  }
`;

/**
 * Everything the profile screen renders, in one round trip — the BFF exists to
 * make that possible (root CLAUDE.md §8).
 */
export const CONTACT_FIELDS = gql`
  ${PHONE_FIELDS}
  fragment ContactFields on Contact {
    id
    kind
    displayName
    firstName
    lastName
    orgName
    birthDate
    gender
    description
    address
    instagramId
    collaborationStatus
    subscriptionType
    photoUrl
    photoAttachmentId
    deletedAt
    messagingOptOut
    fieldOfWork {
      id
      labelFa
    }
    specialty {
      id
      labelFa
    }
    subSpecialty {
      id
      labelFa
    }
    acquisitionSource {
      id
      labelFa
    }
    lifecycleStage {
      id
      labelFa
    }
    salesStage {
      id
      labelFa
    }
    customerHealth {
      id
      labelFa
    }
    engagementStatus {
      id
      labelFa
    }
    city {
      id
      labelFa
    }
    facilityType {
      id
      labelFa
      requiresDetail
    }
    centerName
    professionalCode
    referrerContactId
    referrerName
    isGold
    attachments {
      id
      fileName
      mimeType
      sizeBytes
      createdAt
    }
    phoneNumbers {
      ...PhoneFields
    }
    tags {
      id
      name
      colorHex
      isAssessment
    }
    interests {
      product {
        id
        labelFa
      }
      notes
    }
    customFields {
      id
      name
      labelFa
      type
      options
      isActive
      value
    }
    statusHistory {
      id
      fromStatus
      toStatus
      reason
      changedAt
      changedBy
      changedByName
    }
    affiliations {
      id
      personContactId
      organizationContactId
      personName
      organizationName
      roleTitle
    }
    createdAt
    updatedAt
  }
`;

export const CONTACT_QUERY = gql`
  ${CONTACT_FIELDS}
  query Contact($id: ID!) {
    contact(id: $id) {
      ...ContactFields
    }
  }
`;

export const INTERACTIONS_QUERY = gql`
  query Interactions(
    $contactId: ID!
    $page: Int!
    $pageSize: Int!
    $typeId: ID
    $dateFrom: String
    $dateTo: String
  ) {
    interactions(
      contactId: $contactId
      page: $page
      pageSize: $pageSize
      typeId: $typeId
      dateFrom: $dateFrom
      dateTo: $dateTo
    ) {
      totalCount
      page
      pageSize
      items {
        id
        occurredAt
        description
        status
        outcome
        createdBy
        createdByName
        followUpActivityId
        type {
          id
          labelFa
          code
        }
        attachments {
          id
          fileName
          mimeType
          sizeBytes
        }
      }
    }
  }
`;

export const RECORD_INTERACTION = gql`
  mutation RecordInteraction($input: RecordInteractionInput!) {
    recordInteraction(input: $input) {
      id
      occurredAt
      description
      status
      outcome
      createdByName
      type {
        id
        labelFa
      }
    }
  }
`;

/* ---------- contact mutations (Step 3) ---------- */

export const CREATE_CONTACT = gql`
  mutation CreateContact($input: CreateContactInput!) {
    createContact(input: $input) {
      id
      displayName
    }
  }
`;

export const UPDATE_CONTACT = gql`
  ${CONTACT_FIELDS}
  mutation UpdateContact($id: ID!, $input: UpdateContactInput!) {
    updateContact(id: $id, input: $input) {
      ...ContactFields
    }
  }
`;

export const CHANGE_STATUS = gql`
  ${CONTACT_FIELDS}
  mutation ChangeStatus($contactId: ID!, $toStatus: CollaborationStatus!, $reason: String) {
    changeCollaborationStatus(contactId: $contactId, toStatus: $toStatus, reason: $reason) {
      ...ContactFields
    }
  }
`;

export const ADD_PHONE = gql`
  ${CONTACT_FIELDS}
  mutation AddPhone($contactId: ID!, $phoneNumber: PhoneNumberInput!) {
    addPhoneNumber(contactId: $contactId, phoneNumber: $phoneNumber) {
      ...ContactFields
    }
  }
`;

export const UPDATE_PHONE = gql`
  ${CONTACT_FIELDS}
  mutation UpdatePhone($phoneNumberId: ID!, $numberRaw: String, $label: String, $notes: String) {
    updatePhoneNumber(
      phoneNumberId: $phoneNumberId
      numberRaw: $numberRaw
      label: $label
      notes: $notes
    ) {
      ...ContactFields
    }
  }
`;

export const REMOVE_PHONE = gql`
  ${CONTACT_FIELDS}
  mutation RemovePhone($phoneNumberId: ID!) {
    removePhoneNumber(phoneNumberId: $phoneNumberId) {
      ...ContactFields
    }
  }
`;

export const SET_PRIMARY_PHONE = gql`
  ${CONTACT_FIELDS}
  mutation SetPrimaryPhone($phoneNumberId: ID!) {
    setPrimaryNumber(phoneNumberId: $phoneNumberId) {
      ...ContactFields
    }
  }
`;

export const SET_PHONE_CHANNELS = gql`
  ${CONTACT_FIELDS}
  mutation SetPhoneChannels($phoneNumberId: ID!, $channelCodes: [String!]!) {
    setPhoneNumberChannels(phoneNumberId: $phoneNumberId, channelCodes: $channelCodes) {
      ...ContactFields
    }
  }
`;

export const SET_INTERESTS = gql`
  ${CONTACT_FIELDS}
  mutation SetInterests($contactId: ID!, $productIds: [ID!]!) {
    setInterests(contactId: $contactId, productIds: $productIds) {
      ...ContactFields
    }
  }
`;

export const TAG_CONTACT = gql`
  ${CONTACT_FIELDS}
  mutation TagContact($contactId: ID!, $tagId: ID!) {
    tagContact(contactId: $contactId, tagId: $tagId) {
      ...ContactFields
    }
  }
`;

export const UNTAG_CONTACT = gql`
  ${CONTACT_FIELDS}
  mutation UntagContact($contactId: ID!, $tagId: ID!) {
    untagContact(contactId: $contactId, tagId: $tagId) {
      ...ContactFields
    }
  }
`;

export const SET_AFFILIATION = gql`
  ${CONTACT_FIELDS}
  mutation SetAffiliation(
    $personContactId: ID!
    $organizationContactId: ID!
    $roleTitle: String
  ) {
    setAffiliation(
      personContactId: $personContactId
      organizationContactId: $organizationContactId
      roleTitle: $roleTitle
    ) {
      ...ContactFields
    }
  }
`;

export const REMOVE_AFFILIATION = gql`
  ${CONTACT_FIELDS}
  mutation RemoveAffiliation($personContactId: ID!, $organizationContactId: ID!) {
    removeAffiliation(
      personContactId: $personContactId
      organizationContactId: $organizationContactId
    ) {
      ...ContactFields
    }
  }
`;

export const DELETE_CONTACT = gql`
  mutation DeleteContact($id: ID!, $reason: String) {
    deleteContact(id: $id, reason: $reason) {
      id
      deletedAt
    }
  }
`;

export const UNDELETE_CONTACT = gql`
  mutation UndeleteContact($id: ID!) {
    undeleteContact(id: $id) {
      id
      deletedAt
    }
  }
`;

/* ---------- session ---------- */

export const ME_QUERY = gql`
  query Me {
    me {
      id
      displayName
      role
      bypassed
      # Which halves of the MeshHub app the caller may use: the CRM here, the
      # platform back office at /platform. The BFF enforces both; this only
      # decides what the rail offers.
      access {
        platform
        crm
      }
    }
  }
`;

/* ---------- configuration (settings area) ---------- */

const LOOKUP_FIELDS = gql`
  fragment LookupFields on Lookup {
    id
    kind
    code
    labelFa
    labelEn
    parentId
    isActive
    sortOrder
    isSystem
    requiresDetail
  }
`;

export const ADMIN_LOOKUPS_QUERY = gql`
  ${LOOKUP_FIELDS}
  query AdminLookups($kind: String!, $includeInactive: Boolean!) {
    lookups(kind: $kind, includeInactive: $includeInactive) {
      ...LookupFields
    }
  }
`;

export const CREATE_LOOKUP = gql`
  ${LOOKUP_FIELDS}
  mutation CreateLookup($input: CreateLookupInput!) {
    createLookup(input: $input) {
      ...LookupFields
    }
  }
`;

export const UPDATE_LOOKUP = gql`
  ${LOOKUP_FIELDS}
  mutation UpdateLookup($input: UpdateLookupInput!) {
    updateLookup(input: $input) {
      ...LookupFields
    }
  }
`;

export const SET_LOOKUP_ACTIVE = gql`
  ${LOOKUP_FIELDS}
  mutation SetLookupActive($id: ID!, $isActive: Boolean!) {
    setLookupActive(id: $id, isActive: $isActive) {
      ...LookupFields
    }
  }
`;

export const REORDER_LOOKUPS = gql`
  ${LOOKUP_FIELDS}
  mutation ReorderLookups($kind: String!, $orderedIds: [ID!]!) {
    reorderLookups(kind: $kind, orderedIds: $orderedIds) {
      ...LookupFields
    }
  }
`;

const TAG_FIELDS = gql`
  fragment TagFields on Tag {
    id
    name
    colorHex
    isActive
    isAssessment
  }
`;

export const ADMIN_TAGS_QUERY = gql`
  ${TAG_FIELDS}
  query AdminTags($includeInactive: Boolean!) {
    tags(includeInactive: $includeInactive) {
      ...TagFields
    }
  }
`;

export const CREATE_TAG = gql`
  ${TAG_FIELDS}
  mutation CreateTag($name: String!, $colorHex: String!, $isAssessment: Boolean) {
    createTag(name: $name, colorHex: $colorHex, isAssessment: $isAssessment) {
      ...TagFields
    }
  }
`;

export const UPDATE_TAG = gql`
  ${TAG_FIELDS}
  mutation UpdateTag(
    $id: ID!
    $name: String
    $colorHex: String
    $isAssessment: Boolean
  ) {
    updateTag(id: $id, name: $name, colorHex: $colorHex, isAssessment: $isAssessment) {
      ...TagFields
    }
  }
`;

export const SET_TAG_ACTIVE = gql`
  ${TAG_FIELDS}
  mutation SetTagActive($id: ID!, $isActive: Boolean!) {
    setTagActive(id: $id, isActive: $isActive) {
      ...TagFields
    }
  }
`;

const CUSTOM_FIELD_FIELDS = gql`
  fragment CustomFieldFields on CustomFieldDefinition {
    id
    name
    labelFa
    type
    appliesTo
    options
    isActive
  }
`;

export const CUSTOM_FIELDS_QUERY = gql`
  ${CUSTOM_FIELD_FIELDS}
  query CustomFields($includeInactive: Boolean!) {
    customFieldDefinitions(includeInactive: $includeInactive) {
      ...CustomFieldFields
    }
  }
`;

export const CREATE_CUSTOM_FIELD = gql`
  ${CUSTOM_FIELD_FIELDS}
  mutation CreateCustomField($input: CustomFieldDefinitionInput!) {
    createCustomFieldDefinition(input: $input) {
      ...CustomFieldFields
    }
  }
`;

export const UPDATE_CUSTOM_FIELD = gql`
  ${CUSTOM_FIELD_FIELDS}
  mutation UpdateCustomField($id: ID!, $input: CustomFieldDefinitionInput!) {
    updateCustomFieldDefinition(id: $id, input: $input) {
      ...CustomFieldFields
    }
  }
`;

export const SET_CUSTOM_FIELD_ACTIVE = gql`
  ${CUSTOM_FIELD_FIELDS}
  mutation SetCustomFieldActive($id: ID!, $isActive: Boolean!) {
    setCustomFieldDefinitionActive(id: $id, isActive: $isActive) {
      ...CustomFieldFields
    }
  }
`;

const TEMPLATE_FIELDS = gql`
  fragment TemplateFields on MessageTemplate {
    id
    name
    body
    isActive
    channel {
      id
      code
      labelFa
    }
    category {
      id
      code
      labelFa
    }
  }
`;

export const TEMPLATES_QUERY = gql`
  ${TEMPLATE_FIELDS}
  query Templates($includeInactive: Boolean!) {
    messageTemplates(includeInactive: $includeInactive) {
      ...TemplateFields
    }
  }
`;

export const VALIDATE_PLACEHOLDERS = gql`
  query ValidatePlaceholders($body: String!) {
    validatePlaceholders(body: $body) {
      valid
      allowed
      used
      errors {
        placeholder
        message
        position
      }
    }
  }
`;

export const CREATE_TEMPLATE = gql`
  ${TEMPLATE_FIELDS}
  mutation CreateTemplate($input: MessageTemplateInput!) {
    createMessageTemplate(input: $input) {
      ...TemplateFields
    }
  }
`;

export const UPDATE_TEMPLATE = gql`
  ${TEMPLATE_FIELDS}
  mutation UpdateTemplate($id: ID!, $input: MessageTemplateInput!) {
    updateMessageTemplate(id: $id, input: $input) {
      ...TemplateFields
    }
  }
`;

export const SET_TEMPLATE_ACTIVE = gql`
  ${TEMPLATE_FIELDS}
  mutation SetTemplateActive($id: ID!, $isActive: Boolean!) {
    setMessageTemplateActive(id: $id, isActive: $isActive) {
      ...TemplateFields
    }
  }
`;

export const USERS_QUERY = gql`
  query Users($page: Int!, $pageSize: Int!) {
    users(page: $page, pageSize: $pageSize) {
      totalCount
      page
      pageSize
      items {
        id
        displayName
        role
        isActive
        firstSeenAt
        lastSeenAt
      }
    }
  }
`;

/* ========================================================================
 * Scheduling (Step 4)
 *
 * `Activity.contact` is a nested Contact even though the scheduling service
 * stores only an id — the BFF composes it through a batching loader, which is
 * the one place the two services meet. From here it is just a field.
 * ===================================================================== */

const ACTIVITY_FIELDS = gql`
  fragment ActivityFields on Activity {
    id
    title
    description
    startsAt
    endsAt
    allDay
    status
    resultNote
    resultedAt
    orphaned
    source
    assignedTo
    contactId
    contact {
      id
      displayName
      collaborationStatus
    }
    type {
      id
      code
      labelFa
    }
    reminders {
      id
      remindAt
      channel
      firedAt
    }
    sync {
      googleEventId
      hasConflict
      conflictAt
    }
  }
`;

/**
 * An activity's attachments, on their own.
 *
 * DELIBERATELY NOT in `ActivityFields`. That fragment renders a whole calendar
 * week, and `Activity.attachments` costs one CRM call per activity — putting
 * it in the fragment would buy a field only the edit dialog reads at the price
 * of forty round trips every time somebody pages the week. The dialog asks for
 * it when it opens on an existing activity, and nowhere else.
 */
export const ACTIVITY_ATTACHMENTS_QUERY = gql`
  query ActivityAttachments($id: ID!) {
    activity(id: $id) {
      id
      attachments {
        id
        fileName
        mimeType
        sizeBytes
        createdAt
      }
    }
  }
`;

export const CALENDAR_QUERY = gql`
  ${ACTIVITY_FIELDS}
  query Calendar($dateFrom: String!, $dateTo: String!, $users: [ID!]) {
    calendar(dateFrom: $dateFrom, dateTo: $dateTo, users: $users) {
      ...ActivityFields
    }
  }
`;

export const ACTIVITIES_QUERY = gql`
  ${ACTIVITY_FIELDS}
  query Activities(
    $page: Int
    $pageSize: Int
    $contactId: ID
    $statuses: [ActivityStatus!]
    $dateFrom: String
    $dateTo: String
  ) {
    activities(
      page: $page
      pageSize: $pageSize
      contactId: $contactId
      statuses: $statuses
      dateFrom: $dateFrom
      dateTo: $dateTo
    ) {
      totalCount
      page
      pageSize
      items {
        ...ActivityFields
      }
    }
  }
`;

export const ACTIVITY_QUERY = gql`
  ${ACTIVITY_FIELDS}
  query Activity($id: ID!) {
    activity(id: $id) {
      ...ActivityFields
      checklists {
        id
        title
        templateId
        items {
          id
          title
          isDone
          doneAt
          sortOrder
        }
      }
    }
  }
`;

export const ACTIVITY_TYPES_QUERY = gql`
  query ActivityTypes($includeInactive: Boolean) {
    activityTypes(includeInactive: $includeInactive) {
      id
      code
      labelFa
      labelEn
      isActive
      isSystem
      sortOrder
    }
  }
`;

/**
 * The activity-type vocabulary, administered.
 *
 * `refetchQueries` names the OPERATION rather than a `{ query, variables }`
 * pair, so every mounted `ActivityTypes` query refreshes whichever variables
 * it was given — the calendar's dialog asks with none, the settings screen asks
 * with `includeInactive`. The caller pairs it with `awaitRefetchQueries` when
 * it intends to SELECT what it just created, because until the refetch lands
 * the option does not exist and the select would point at nothing.
 */
export const CREATE_ACTIVITY_TYPE = gql`
  mutation CreateActivityType($input: ActivityTypeInput!) {
    createActivityType(input: $input) {
      id
      code
      labelFa
      labelEn
      isActive
      isSystem
      sortOrder
    }
  }
`;

export const UPDATE_ACTIVITY_TYPE = gql`
  mutation UpdateActivityType($id: ID!, $input: UpdateActivityTypeInput!) {
    updateActivityType(id: $id, input: $input) {
      id
      code
      labelFa
      labelEn
      isActive
      isSystem
      sortOrder
    }
  }
`;

export const SET_ACTIVITY_TYPE_ACTIVE = gql`
  mutation SetActivityTypeActive($id: ID!, $isActive: Boolean!) {
    setActivityTypeActive(id: $id, isActive: $isActive) {
      id
      isActive
    }
  }
`;

export const CREATE_ACTIVITY = gql`
  ${ACTIVITY_FIELDS}
  mutation CreateActivity($input: ActivityInput!) {
    createActivity(input: $input) {
      ...ActivityFields
    }
  }
`;

export const UPDATE_ACTIVITY = gql`
  ${ACTIVITY_FIELDS}
  mutation UpdateActivity($id: ID!, $input: ActivityInput!) {
    updateActivity(id: $id, input: $input) {
      ...ActivityFields
    }
  }
`;

export const CHANGE_ACTIVITY_STATUS = gql`
  ${ACTIVITY_FIELDS}
  mutation ChangeActivityStatus(
    $id: ID!
    $toStatus: ActivityStatus!
    $resultNote: String
    $reason: String
  ) {
    changeActivityStatus(
      id: $id
      toStatus: $toStatus
      resultNote: $resultNote
      reason: $reason
    ) {
      ...ActivityFields
    }
  }
`;

export const DELETE_ACTIVITY = gql`
  mutation DeleteActivity($id: ID!) {
    deleteActivity(id: $id) {
      id
      deletedAt
    }
  }
`;

export const SET_REMINDERS = gql`
  ${ACTIVITY_FIELDS}
  mutation SetReminders($activityId: ID!, $reminders: [ReminderInput!]!) {
    setReminders(activityId: $activityId, reminders: $reminders) {
      ...ActivityFields
    }
  }
`;

export const CREATE_FOLLOW_UP_FROM_INTERACTION = gql`
  ${ACTIVITY_FIELDS}
  mutation CreateFollowUp($interactionId: ID!, $input: ActivityInput!) {
    createFollowUpFromInteraction(interactionId: $interactionId, input: $input) {
      activity {
        ...ActivityFields
      }
      interaction {
        id
        followUpActivityId
      }
    }
  }
`;

export const FOLLOW_UPS_QUERY = gql`
  query FollowUps($state: SuggestionState) {
    followUpSuggestions(state: $state) {
      id
      reason
      state
      suggestedAt
      contactId
      contact {
        id
        displayName
      }
      activity {
        id
        title
        startsAt
        type {
          id
          labelFa
        }
      }
    }
  }
`;

export const ACCEPT_SUGGESTION = gql`
  mutation AcceptSuggestion($id: ID!, $input: ActivityInput!) {
    acceptSuggestion(id: $id, input: $input) {
      id
      state
      createdActivityId
    }
  }
`;

export const DISMISS_SUGGESTION = gql`
  mutation DismissSuggestion($id: ID!) {
    dismissSuggestion(id: $id) {
      id
      state
    }
  }
`;

export const DUE_REMINDERS_QUERY = gql`
  query DueReminders($since: String) {
    dueReminders(since: $since) {
      reminder {
        id
        remindAt
        firedAt
      }
      activity {
        id
        title
        startsAt
        contact {
          id
          displayName
        }
      }
    }
  }
`;

const CHECKLIST_FIELDS = gql`
  fragment ChecklistFields on Checklist {
    id
    title
    ownerType
    ownerId
    templateId
    items {
      id
      title
      isDone
      doneAt
      sortOrder
    }
  }
`;

export const CHECKLISTS_QUERY = gql`
  ${CHECKLIST_FIELDS}
  query Checklists($ownerType: ChecklistOwner!, $ownerId: ID!) {
    checklists(ownerType: $ownerType, ownerId: $ownerId) {
      ...ChecklistFields
    }
  }
`;

export const CHECKLIST_TEMPLATES_QUERY = gql`
  query ChecklistTemplates($includeInactive: Boolean) {
    checklistTemplates(includeInactive: $includeInactive) {
      id
      title
      description
      isActive
      isSystem
      items {
        id
        title
        sortOrder
      }
    }
  }
`;

export const CREATE_CHECKLIST_TEMPLATE = gql`
  mutation CreateChecklistTemplate($input: ChecklistTemplateInput!) {
    createChecklistTemplate(input: $input) {
      id
    }
  }
`;

export const UPDATE_CHECKLIST_TEMPLATE = gql`
  mutation UpdateChecklistTemplate($id: ID!, $input: ChecklistTemplateInput!) {
    updateChecklistTemplate(id: $id, input: $input) {
      id
    }
  }
`;

export const SET_CHECKLIST_TEMPLATE_ACTIVE = gql`
  mutation SetChecklistTemplateActive($id: ID!, $isActive: Boolean!) {
    setChecklistTemplateActive(id: $id, isActive: $isActive) {
      id
      isActive
    }
  }
`;

export const CREATE_CHECKLIST = gql`
  ${CHECKLIST_FIELDS}
  mutation CreateChecklist(
    $ownerType: ChecklistOwner!
    $ownerId: ID!
    $title: String
    $templateId: ID
  ) {
    createChecklist(
      ownerType: $ownerType
      ownerId: $ownerId
      title: $title
      templateId: $templateId
    ) {
      ...ChecklistFields
    }
  }
`;

export const TOGGLE_CHECKLIST_ITEM = gql`
  ${CHECKLIST_FIELDS}
  mutation ToggleChecklistItem($itemId: ID!, $isDone: Boolean!) {
    toggleChecklistItem(itemId: $itemId, isDone: $isDone) {
      ...ChecklistFields
    }
  }
`;

export const ADD_CHECKLIST_ITEM = gql`
  ${CHECKLIST_FIELDS}
  mutation AddChecklistItem($checklistId: ID!, $title: String!) {
    addChecklistItem(checklistId: $checklistId, title: $title) {
      ...ChecklistFields
    }
  }
`;

const GOOGLE_CONNECTION_FIELDS = gql`
  fragment GoogleConnectionFields on GoogleConnection {
    configured
    connected
    googleEmail
    calendarId
    connectedAt
    lastSyncAt
    lastError
    connectUrl
    recent {
      direction
      entity
      outcome
      error
      at
    }
  }
`;

export const GOOGLE_CONNECTION_QUERY = gql`
  ${GOOGLE_CONNECTION_FIELDS}
  query GoogleConnection {
    googleConnection {
      ...GoogleConnectionFields
    }
  }
`;

export const SYNC_GOOGLE_NOW = gql`
  ${GOOGLE_CONNECTION_FIELDS}
  mutation SyncGoogleNow {
    syncGoogleNow {
      pushed
      pulled
      imported
      conflicts
      tasks
      errors
      skippedReason
      connection {
        ...GoogleConnectionFields
      }
    }
  }
`;

export const DISCONNECT_GOOGLE = gql`
  ${GOOGLE_CONNECTION_FIELDS}
  mutation DisconnectGoogle {
    disconnectGoogle {
      ...GoogleConnectionFields
    }
  }
`;

export const SET_GOOGLE_CALENDAR = gql`
  ${GOOGLE_CONNECTION_FIELDS}
  mutation SetGoogleCalendar($calendarId: String!) {
    setGoogleCalendar(calendarId: $calendarId) {
      ...GoogleConnectionFields
    }
  }
`;
